import React, { useEffect, useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { mapDataToLabelValue } from "../../../../helpers";
import { validationSchema } from "../schema";
import { editCoupon } from "../../../../redux/features/CouponSlice";
import { fetchUsersAsync } from "../../../../redux/features/adminSlice";

interface EditCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedCoupon: any;
}

const EditCouponModal: React.FC<EditCouponModalProps> = ({
  open,
  onClose,
  onSave,
  selectedCoupon,
}) => {
  const dispatch = useAppDispatch();
//   const { vendors } = useAppSelector((state: RootState) => state.admin);
  const { loading } = useAppSelector((state: RootState) => state.coupons);

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountPercentage: "",
    vendorId: "",
  });

  const [errors, setErrors] = useState({
    code: "",
    description: "",
    discountPercentage: "",
    vendorId: "",
  });

  useEffect(() => {
    dispatch(
      fetchUsersAsync({
        page: 1,
        limit: 100,
        userType: "VENDOR",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (selectedCoupon) {
      setFormData({
        code: selectedCoupon?.code || "",
        description: selectedCoupon?.description || "",
        discountPercentage: selectedCoupon?.discountPercentage || "",
        vendorId: selectedCoupon?.vendor?.id || "",
      });
    }
  }, [selectedCoupon]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleGenerateCoupon = () => {
    const randomCode = Math.random()
      .toString(36)
      .substring(2, 10)
      .toUpperCase();
    setFormData((prev) => ({ ...prev, code: randomCode }));
    setErrors((prev) => ({ ...prev, code: "" }));
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    const result = await dispatch(
      editCoupon({ couponId: selectedCoupon.id, payload: formData })
    );

    if (result?.meta?.requestStatus === "fulfilled") {
      toast.success("Coupon updated successfully");
      onClose();
      onSave();
    } else {
      toast.error("Failed to update coupon");
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            type="submit"
            label="Save Changes"
            onClick={handleSubmit}
            isLoading={loading}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="p-6">
        {/* Coupon Code with Optional Generate Button */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Coupon Code <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="Enter coupon code"
              className={`w-full border rounded px-3 py-2 text-sm ${
                errors.code ? "border-red-500" : "border-gray-300"
              }`}
              required
            />
            <button
              type="button"
              onClick={handleGenerateCoupon}
              className="text-sm whitespace-nowrap bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Generate
            </button>
          </div>
          {errors.code && (
            <p className="text-sm text-red-500 mt-1">{errors.code}</p>
          )}
        </div>

        {/* Description */}
        <ModalInput
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter description"
          error={errors.description}
          required
        />

        {/* Discount Percentage */}
        <ModalInput
          label="Discount Percentage (%)"
          name="discountPercentage"
          type="number"
          value={formData.discountPercentage}
          onChange={handleChange}
          placeholder="Enter discount percentage"
          error={errors.discountPercentage}
          required
        />

        {/* Vendor Dropdown */}
        <Dropdown
          label="Vendor"
          name="vendorId"
          options={mapDataToLabelValue([], "name", "id")}
          placeholder="Select vendor"
          value={formData.vendorId}
          onSelect={(value) => handleSelectChange("vendorId", value)}
          error={errors.vendorId}
          required
        />
      </form>
    </CustomModalContainer>
  );
};

export default EditCouponModal;
